import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Crown, Check, X, Zap, Phone, Headphones, Building2, ExternalLink, Loader2 } from "lucide-react";
import { useWorkspaceSubscription, isSubscriptionEntitled } from "@/hooks/use-workspace-subscription";
import {
  useWorkspaceBilling,
  useSubscribePro,
  useCreatePortalSession,
  useCanManageBilling,
  SUBSCRIBE_PHASE_LABEL,
  type BillingError,
} from "@/hooks/use-workspace-billing";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { getWorkspaceUsageRate, KB_RATE_PER_MINUTE } from "@/lib/billing-config";
import { formatMoney } from "@/lib/money";
import { isFeatureOn } from "@/services/feature-flags.service";

const STATUS_LABELS: Record<string, string> = {
  active: 'Pro',
  trialing: 'Trial',
  past_due: 'Past due',
  unpaid: 'Unpaid',
  canceled: 'Canceled',
  expired: 'Expired',
};

export function SubscriptionUpgrade() {
  const { data: subscription, isLoading: subscriptionLoading, isFetching: subscriptionFetching } = useWorkspaceSubscription();
  const { data: workspaceBilling, isLoading: billingLoading } = useWorkspaceBilling();
  const subscribePro = useSubscribePro();
  const createPortalSession = useCreatePortalSession();
  const canManage = useCanManageBilling();
  // One subscribe attempt (contract C7): kept while that attempt is retried.
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());

  const isActive = isSubscriptionEntitled(subscription);
  // past_due / unpaid: the subscription still exists, so the fix is the billing
  // portal, not another subscription request (which would 409).
  const needsBillingFix = subscription?.status === 'past_due' || subscription?.status === 'unpaid';
  const hasPaymentMethod = workspaceBilling?.payment_method_added ?? false;
  const usageRate = formatMoney(getWorkspaceUsageRate(workspaceBilling));
  // The limit calls are held to (E2-10), not the one the subscription row advertises.
  const concurrencyLimit = workspaceBilling?.call_concurrency_limit ?? 10;

  const proFeatures = [
    { icon: Building2, name: "Multiple workspaces", description: "Organize teams and projects" },
    { icon: Phone, name: "Campaigns & batch calling", description: "Run outbound campaigns at scale" },
    { icon: Zap, name: "30 concurrent calls", description: "Scale your operations" },
    { icon: Headphones, name: "Priority Support", description: "Get help when you need it" },
  ];

  const freeFeatures = [
    { name: "10 concurrent calls", included: true },
    { name: "Usage-based billing", included: true },
    { name: "Inbound & outbound calls", included: true },
    { name: "Multiple workspaces", included: false },
    { name: "Campaigns & batch calling", included: false },
    { name: "Priority support", included: false },
  ];

  // Only an attempt that got no answer at all is retried as itself: it may have created the
  // subscription. Any answer settles it; the next attempt's new id still finds an upgrade whose
  // payment was never confirmed, and the server resumes that one. The hook toasts every failure.
  const handleUpgrade = () =>
    subscribePro.mutate(
      { requestId },
      {
        onSuccess: () => setRequestId(crypto.randomUUID()),
        onError: (error: BillingError) => {
          if (error.status !== 0) setRequestId(crypto.randomUUID());
        },
      },
    );

  const isLoading = subscriptionLoading || billingLoading;
  // Until the plan is re-read after an upgrade, the card cannot tell it is Pro yet.
  const upgradeLocked = subscribePro.running || isLoading || subscriptionFetching;
  const upgradeLabel = subscribePro.phase
    ? SUBSCRIBE_PHASE_LABEL[subscribePro.phase]
    : subscribePro.running
      ? SUBSCRIBE_PHASE_LABEL.starting
      : "Upgrade to Pro";

  const statusLabel = STATUS_LABELS[subscription?.status] ?? 'Pay As You Go';

  const formatDate = (value: string) => new Date(value).toLocaleDateString();
  const renewalNote = !subscription
    ? null
    : needsBillingFix
      ? { label: 'Billing', value: 'Past due — update your card' }
      : subscription.canceled_at
        ? { label: 'Ends on', value: formatDate(subscription.current_period_end) }
        : isActive && subscription.current_period_end
          ? { label: 'Renews on', value: formatDate(subscription.current_period_end) }
          : null;

  return (
    <div className="space-y-6">
      {/* Pro Plan Card */}
      <Card className={`relative overflow-hidden transition-all duration-300 ${
        isActive 
          ? "border-primary/50 bg-gradient-to-br from-primary/5 via-background to-primary/10 shadow-lg shadow-primary/5" 
          : "border-2 hover:border-primary/30 hover:shadow-md"
      }`}>
        {isActive && (
          <div className="absolute top-0 right-0 px-4 py-1.5 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground text-xs font-semibold rounded-bl-xl">
            Active
          </div>
        )}
        <CardHeader className="pb-4">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center shadow-inner">
              <Crown className="w-7 h-7 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-2xl font-bold">Pro Plan</CardTitle>
              <CardDescription className="mt-1 text-base">
                Everything you need to scale your voice AI operations
              </CardDescription>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold tracking-tight">$120</span>
                <span className="text-muted-foreground text-lg">/month</span>
                <span className="text-sm text-muted-foreground ml-3 px-2 py-0.5 bg-muted rounded-full">+ {usageRate}/min</span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground mb-4">Everything in Pay As You Go, plus:</p>
            <div className="grid gap-3">
              {proFeatures.map((feature) => (
                <div key={feature.name} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{feature.name}</p>
                    <p className="text-xs text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {!canManage ? null : isActive || needsBillingFix ? (
            <div className="space-y-3 pt-2">
              <Button 
                onClick={() => createPortalSession.mutate()}
                variant="outline"
                disabled={createPortalSession.isPending}
                className="w-full h-12 text-base border-primary/20 hover:bg-primary/5 hover:border-primary/40 transition-all"
              >
                {createPortalSession.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Opening portal...
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Manage Subscription
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Update payment method, view invoices, or cancel
              </p>
            </div>
          ) : (
            <>
              {!hasPaymentMethod ? (
                <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50">
                  <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <AlertDescription className="text-amber-700 dark:text-amber-300">
                    Please add a payment method in the Payment tab before upgrading to Pro.
                  </AlertDescription>
                </Alert>
              ) : (
                <Button 
                  onClick={handleUpgrade} 
                  disabled={upgradeLocked} 
                  className="w-full h-12 text-base bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-lg shadow-primary/20 transition-all"
                  size="lg"
                >
                  {subscribePro.running && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {upgradeLabel}
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Current Plan Summary */}
      <Card className="border-muted/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold">Current Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-border/50">
              <span className="text-muted-foreground">Status</span>
              <Badge
                variant={isActive ? "default" : needsBillingFix ? "destructive" : "secondary"}
                className={isActive ? "bg-gradient-to-r from-primary to-primary/80 shadow-sm" : ""}
              >
                {statusLabel}
              </Badge>
            </div>
            {renewalNote && (
              <div className="flex items-center justify-between pb-3 border-b border-border/50">
                <span className="text-muted-foreground">{renewalNote.label}</span>
                <span className={`font-medium ${needsBillingFix ? "text-destructive" : ""}`}>
                  {renewalNote.value}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Concurrent calls</span>
              <span className="font-semibold text-lg">{concurrencyLimit}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Free Plan Features */}
      {!isActive && !needsBillingFix && (
        <Card className="border-dashed border-muted-foreground/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <span>Pay As You Go</span>
              <Badge variant="secondary" className="font-normal">Current</Badge>
            </CardTitle>
            <CardDescription className="text-base">{usageRate}/minute • No monthly fee</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2.5">
              {freeFeatures.map((feature) => (
                <div key={feature.name} className="flex items-center gap-3">
                  {feature.included ? (
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
                      <X className="w-3 h-3 text-muted-foreground" />
                    </div>
                  )}
                  <span className={feature.included ? "font-medium" : "text-muted-foreground"}>
                    {feature.name}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Priced only while shared/feature-flags.json offers automatic search. */}
      {isFeatureOn("kbAutoSearch") && (
        <p className="text-sm text-muted-foreground">
          Calls that use automatic knowledge-base search add {formatMoney(KB_RATE_PER_MINUTE)}/min.
        </p>
      )}
    </div>
  );
}
